import type { AttendanceSession, AttendanceRunMatrix, CredentialEmailBatchResult, SessionRoster, SessionCheckInInput, SessionCheckInResult, SessionCheckInStatus } from './attendance-session-types';
import type {
  AttendanceCorrectionRecord,
  AttendancePolicyDefinition,
  AttendancePolicyView,
  AttendanceEventKind,
  AttendanceEventRecord,
  AttendanceSummary,
  AuthContextResponse,
  OrganizationAccountView,
  OrganizationMemberView,
  IdentityRoleCatalogEntry,
  PrivacyPolicyKind,
  PrivacyPolicyContent,
  PrivacyPolicyView,
  OrganizationBrandingContent,
  ActiveOrganizationBrandingView,
  AdminOrganizationBrandingView,
  BrandingAsset,
  CreatePlatformTenantInput,
  EdocationHandoffListView,
  EdocationHandoffRecord,
  ExternalGroupMappingRecord,
  EnrollmentIssuedToken,
  EnrollmentRecord,
  EventInstanceDetail,
  EventInstanceRecord,
  EventInstanceState,
  EventResponsiblePersonRecord,
  EventTypeRecord,
  EventScheduleInput,
  EventScheduleItem,
  GroupMappingAdminView,
  IdentityEffectiveAccessRecord,
  ListEnvelope,
  LocalizedText,
  MeAccessDiagnosticsResponse,
  MeResponse,
  NotificationChannel,
  NotificationDeliveryRecord,
  NotificationDeliveryStatus,
  NotificationJobRecord,
  NotificationJobStatus,
  NotificationTemplateRecord,
  EventReportingView,
  ParticipantTaskView,
  PlatformTenantRecord,
  PlatformTenantStatus,
  ReviewerTaskView,
  TaskRuntimeDiagnosticsView,
  TaskSubmissionRecord,
  TaskUploadRecord,
} from './types';

/**
 * Thin fetch wrapper for the Event API.
 *
 * - The base URL is configured via `VITE_API_BASE_URL` (default `/api`).
 * - The `x-tenant-id` header is set automatically from the active session.
 * - In dev the `x-dev-user` bypass header is forwarded so the Nest
 *   middleware can resolve a seeded principal without a real IdP.
 *
 * The client intentionally stays dependency-free (no axios / ky / tanstack)
 * to keep the bootstrap web slice boringly small. If we later need caching
 * or retries we'll swap in a real data-fetching layer.
 */

export interface ApiSession {
  readonly tenantCode: string;
  readonly devUser?: string;
  readonly bearerToken?: string;
  /** Resolve/refresh the same identity's token immediately before a request.
   * Old session closures must reject after sign-out/identity replacement.
   */
  readonly getBearerToken?: () => Promise<string>;
  readonly locale?: string;
}

export interface ApiClient {
  getAuthContext(tenantCode?: string): Promise<AuthContextResponse>;
  getMe(session: ApiSession): Promise<MeResponse>;
  getAccount(session: ApiSession): Promise<OrganizationAccountView>;
  createOrganization(session: ApiSession, input: { readonly displayName: string; readonly defaultLocale: string }): Promise<MeResponse['tenant']>;
  listOrganizationMembers(session: ApiSession): Promise<{ readonly items: ReadonlyArray<OrganizationMemberView> }>;
  changeOrganizationMember(session: ApiSession, input: { readonly externalSubject: string; readonly roles: ReadonlyArray<string>; readonly active: boolean }): Promise<{ readonly id: string }>;
  listOrganizationRoles(session: ApiSession): Promise<ListEnvelope<IdentityRoleCatalogEntry>>;
  listPrivacyRoles(session: ApiSession): Promise<ListEnvelope<IdentityRoleCatalogEntry>>;
  changePrivacyOfficer(session: ApiSession, input: { readonly memberId: string; readonly enabled: boolean; readonly authorityConfirmed: true; readonly reason: string }): Promise<{ readonly id: string; readonly enabled: boolean }>;
  listPrivacyPolicies(session: ApiSession): Promise<ListEnvelope<PrivacyPolicyView>>;
  getPrivacyPolicy(session: ApiSession, kind: PrivacyPolicyKind): Promise<PrivacyPolicyView>;
  savePrivacyRevision(session: ApiSession, kind: PrivacyPolicyKind, input: PrivacyPolicyContent & { readonly expectedVersion: number }): Promise<PrivacyPolicyView>;
  decidePrivacyPolicy(session: ApiSession, kind: PrivacyPolicyKind, input: { readonly expectedVersion: number; readonly revisionId: string; readonly decision: 'approve' | 'revoke'; readonly reason: string; readonly authorityConfirmed: true }): Promise<PrivacyPolicyView>;
  getMeAccessDiagnostics(session: ApiSession): Promise<MeAccessDiagnosticsResponse>;
  getOrganizationBranding(session: ApiSession): Promise<ActiveOrganizationBrandingView>;
  getAdminOrganizationBranding(session: ApiSession): Promise<AdminOrganizationBrandingView>;
  saveOrganizationBranding(session: ApiSession, input: OrganizationBrandingContent & { readonly expectedVersion: number }): Promise<AdminOrganizationBrandingView>;
  applyOrganizationBranding(session: ApiSession, input: { readonly expectedVersion: number }): Promise<AdminOrganizationBrandingView>;
  uploadBrandingAsset(session: ApiSession, input: { readonly filename: string; readonly contentType: string; readonly dataBase64: string; readonly purpose?: 'logo' | 'hero' }, options?: { readonly signal?: AbortSignal }): Promise<BrandingAsset>;
  getBrandingAsset(session: ApiSession, id: string, options?: { readonly signal?: AbortSignal }): Promise<Blob>;

  listPlatformTenants(
    session: ApiSession,
  ): Promise<ReadonlyArray<PlatformTenantRecord>>;
  getPlatformTenant(
    session: ApiSession,
    id: string,
  ): Promise<PlatformTenantRecord>;
  createPlatformTenant(
    session: ApiSession,
    input: CreatePlatformTenantInput,
  ): Promise<PlatformTenantRecord>;
  updatePlatformTenantStatus(
    session: ApiSession,
    id: string,
    status: PlatformTenantStatus,
  ): Promise<PlatformTenantRecord>;

  listEventTypes(session: ApiSession): Promise<ReadonlyArray<EventTypeRecord>>;
  saveEventType(session: ApiSession, code: string, input: Pick<EventTypeRecord, 'labels' | 'supportsEnrollment' | 'participationMode' | 'defaultCapacity' | 'defaultWaitlistEnabled' | 'status'>): Promise<EventTypeRecord>;
  listEvents(
    session: ApiSession,
    filter?: { readonly state?: ReadonlyArray<EventInstanceState> },
  ): Promise<ReadonlyArray<EventInstanceRecord>>;
  getEvent(session: ApiSession, id: string): Promise<EventInstanceDetail>;
  saveEventResponsible(session: ApiSession, eventId: string, input: {
    readonly userId: string;
    readonly role: 'organizer';
    readonly position: number;
  }): Promise<EventResponsiblePersonRecord>;
  saveScheduleItem(session: ApiSession, eventId: string, input: EventScheduleInput, itemId?: string): Promise<EventScheduleItem>;
  createEvent(
    session: ApiSession,
    input: CreateEventInput,
  ): Promise<EventInstanceRecord>;
  updateEvent(
    session: ApiSession,
    id: string,
    patch: UpdateEventInput,
  ): Promise<EventInstanceRecord>;
  transitionEvent(
    session: ApiSession,
    id: string,
    to: EventInstanceState,
  ): Promise<EventInstanceRecord>;

  listEnrollments(
    session: ApiSession,
    filter: ListEnrollmentsFilter,
  ): Promise<ReadonlyArray<EnrollmentRecord>>;
  listMyEnrollments(session: ApiSession): Promise<ReadonlyArray<EnrollmentRecord>>;
  enrollSelf(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<EnrollmentRecord>;
  enrollAdmin(
    session: ApiSession,
    input: { readonly eventInstanceId: string; readonly userId: string },
  ): Promise<EnrollmentRecord>;
  cancelEnrollment(
    session: ApiSession,
    id: string,
    reason?: string,
  ): Promise<{
    readonly cancelled: EnrollmentRecord;
    readonly promoted?: EnrollmentRecord;
  }>;
  issueToken(
    session: ApiSession,
    enrollmentId: string,
  ): Promise<EnrollmentIssuedToken>;

  listAttendanceSessions(session: ApiSession, eventId: string): Promise<ListEnvelope<AttendanceSession>>;
  createAttendanceSession(session: ApiSession, eventId: string, scheduleItemId: string): Promise<AttendanceSession>;
  openAttendanceSession(session: ApiSession, id: string, expectedRevision: number): Promise<AttendanceSession>;
  closeAttendanceSession(session: ApiSession, id: string, input: { readonly expectedRevision: number; readonly confirmAbsences: boolean; readonly reason: string }): Promise<AttendanceSession>;
  reopenAttendanceSession(session: ApiSession, id: string, input: { readonly expectedRevision: number; readonly reason: string }): Promise<AttendanceSession>;
  cancelAttendanceSession(session: ApiSession, id: string, input: { readonly expectedRevision: number; readonly reason: string }): Promise<AttendanceSession>;
  getAttendanceSessionRoster(session: ApiSession, id: string): Promise<SessionRoster>;
  addAttendanceSessionParticipant(session: ApiSession, id: string, input: { readonly enrollmentId: string; readonly expectedRevision: number; readonly reason: string }): Promise<unknown>;
  correctAttendanceSessionRecord(session: ApiSession, id: string, enrollmentId: string, input: { readonly state: 'present' | 'absent' | 'not_recorded'; readonly expectedRevision: number; readonly reason: string }): Promise<unknown>;
  checkInAttendanceSession(session: ApiSession, id: string, input: SessionCheckInInput): Promise<SessionCheckInResult>;
  getAttendanceSessionCheckIn(session: ApiSession, id: string, operationId: string): Promise<SessionCheckInStatus>;
  getAttendanceRunMatrix(session: ApiSession, eventId: string): Promise<AttendanceRunMatrix>;
  downloadAttendanceRunCsv(session: ApiSession, eventId: string): Promise<DownloadedFile>;
  getAttendanceRunPrintSnapshot(session: ApiSession, eventId: string): Promise<AttendanceRunMatrix>;
  emailEnrollmentCredential(session: ApiSession, enrollmentId: string, requestId: string): Promise<{ readonly jobId: string; readonly status: string; readonly created: boolean }>;
  emailEnrollmentCredentialsBatch(session: ApiSession, enrollmentIds: ReadonlyArray<string>, requestId: string): Promise<CredentialEmailBatchResult>;
  rotateEnrollmentCredential(session: ApiSession, enrollmentId: string, reason: string): Promise<EnrollmentIssuedToken>;
  revokeEnrollmentCredential(session: ApiSession, enrollmentId: string, reason: string): Promise<{ readonly revoked: number }>;

  checkInAttendance(
    session: ApiSession,
    input: CheckInAttendanceInput,
  ): Promise<AttendanceEventRecord>;
  listAttendanceEvents(
    session: ApiSession,
    filter: ListAttendanceEventsFilter,
  ): Promise<ReadonlyArray<AttendanceEventRecord>>;
  getAttendanceSummary(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<AttendanceSummary>;
  getAttendancePolicy(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<AttendancePolicyView>;
  upsertAttendancePolicy(
    session: ApiSession,
    eventInstanceId: string,
    input: AttendancePolicyDefinition,
  ): Promise<AttendancePolicyView>;
  removeAttendancePolicy(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<void>;
  correctAttendance(
    session: ApiSession,
    input: CorrectAttendanceInput,
  ): Promise<{
    readonly correction: AttendanceCorrectionRecord;
    readonly attendanceEvent: AttendanceEventRecord;
  }>;
  voidAttendance(
    session: ApiSession,
    attendanceEventId: string,
    reason: string,
  ): Promise<{
    readonly correction: AttendanceCorrectionRecord;
    readonly voided: AttendanceEventRecord;
  }>;
  listAttendanceCorrections(
    session: ApiSession,
    filter: { readonly eventInstanceId?: string; readonly enrollmentId?: string },
  ): Promise<ReadonlyArray<AttendanceCorrectionRecord>>;

  listMyTasks(
    session: ApiSession,
    enrollmentId: string,
  ): Promise<ReadonlyArray<ParticipantTaskView>>;
  getMyTask(
    session: ApiSession,
    enrollmentId: string,
    assignmentId: string,
  ): Promise<ParticipantTaskView>;
  saveMyTask(
    session: ApiSession,
    enrollmentId: string,
    assignmentId: string,
    input: { readonly content: Readonly<Record<string, unknown>>; readonly submit?: boolean },
  ): Promise<TaskSubmissionRecord>;
  submitTest(
    session: ApiSession,
    enrollmentId: string,
    assignmentId: string,
    answers: Readonly<Record<string, string | number | boolean>>,
  ): Promise<TaskSubmissionRecord>;
  submitDocument(
    session: ApiSession,
    enrollmentId: string,
    assignmentId: string,
    note?: string,
  ): Promise<TaskSubmissionRecord>;
  attachUpload(
    session: ApiSession,
    enrollmentId: string,
    assignmentId: string,
    input: UploadAttachInput,
  ): Promise<TaskUploadRecord>;
  removeUpload(
    session: ApiSession,
    enrollmentId: string,
    uploadId: string,
  ): Promise<void>;

  listReviewQueue(
    session: ApiSession,
    filter?: { readonly eventInstanceId?: string },
  ): Promise<ReadonlyArray<ReviewerTaskView>>;
  getTaskRuntimeDiagnostics(
    session: ApiSession,
  ): Promise<TaskRuntimeDiagnosticsView>;
  getReviewSubmission(
    session: ApiSession,
    submissionId: string,
  ): Promise<ReviewerTaskView>;
  recordReviewDecision(
    session: ApiSession,
    submissionId: string,
    input: ReviewDecisionInput,
  ): Promise<{
    readonly submission: TaskSubmissionRecord;
    readonly review: unknown;
  }>;

  listNotificationTemplates(
    session: ApiSession,
    filter?: {
      readonly channel?: NotificationChannel;
      readonly enabled?: boolean;
    },
  ): Promise<ReadonlyArray<NotificationTemplateRecord>>;
  listNotificationJobs(
    session: ApiSession,
    filter?: ListNotificationJobsFilter,
  ): Promise<ReadonlyArray<NotificationJobRecord>>;
  listNotificationJobDeliveries(
    session: ApiSession,
    jobId: string,
  ): Promise<ReadonlyArray<NotificationDeliveryRecord>>;
  listNotificationDeliveries(
    session: ApiSession,
    filter?: {
      readonly status?: ReadonlyArray<NotificationDeliveryStatus>;
      readonly limit?: number;
    },
  ): Promise<ReadonlyArray<NotificationDeliveryRecord>>;
  cancelNotificationJob(
    session: ApiSession,
    id: string,
    reason: string,
  ): Promise<NotificationJobRecord>;

  listIdentityGroupMappings(session: ApiSession): Promise<GroupMappingAdminView>;
  createIdentityGroupMapping(
    session: ApiSession,
    input: UpsertIdentityGroupMappingInput,
  ): Promise<ExternalGroupMappingRecord>;
  updateIdentityGroupMapping(
    session: ApiSession,
    id: string,
    input: UpsertIdentityGroupMappingInput,
  ): Promise<ExternalGroupMappingRecord>;
  listIdentityEffectiveAccess(
    session: ApiSession,
  ): Promise<ReadonlyArray<IdentityEffectiveAccessRecord>>;

  getEventReport(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<EventReportingView>;
  downloadEventReportCsv(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<DownloadedFile>;
  downloadEventReportPdf(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<DownloadedFile>;
  listEdocationHandoffView(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<EdocationHandoffListView>;
  triggerEdocationHandoff(
    session: ApiSession,
    eventInstanceId: string,
  ): Promise<EdocationHandoffRecord>;
}

export interface DownloadedFile {
  readonly filename: string;
  readonly contentType: string;
  readonly blob: Blob;
}

export interface ListNotificationJobsFilter {
  readonly status?: ReadonlyArray<NotificationJobStatus>;
  readonly triggerType?: string;
  readonly triggerResourceId?: string;
  readonly recipientUserId?: string;
  readonly limit?: number;
}

export interface UploadAttachInput {
  readonly filename: string;
  readonly contentType: string;
  readonly contentBase64: string;
}

export interface ReviewDecisionInput {
  readonly action: 'accept' | 'reject' | 'return_for_rework' | 'comment';
  readonly comment?: string;
  readonly reason?: string;
  readonly score?: number | null;
  readonly scoreMax?: number | null;
}

export interface UpsertIdentityGroupMappingInput {
  readonly directoryGroupCode: string;
  readonly roleCode: string;
  readonly enabled: boolean;
}

export interface CreateEventInput {
  readonly eventTypeId: string;
  readonly code: string;
  readonly titles: LocalizedText;
  readonly summaries?: LocalizedText;
  readonly descriptions?: LocalizedText;
  readonly capacity?: number | null;
  readonly waitlistEnabled?: boolean;
  readonly registrationOpensAt?: string | null;
  readonly registrationClosesAt?: string | null;
  readonly startsAt?: string | null;
  readonly endsAt?: string | null;
  readonly timezone?: string;
  readonly locationName?: string;
  readonly locationAddress?: string;
  readonly locationUrl?: string;
}

export type UpdateEventInput = Partial<Omit<CreateEventInput, 'eventTypeId' | 'code'>>;

export interface ListEnrollmentsFilter {
  readonly eventInstanceId?: string;
  readonly userId?: string;
  readonly status?: ReadonlyArray<EnrollmentRecord['status']>;
}

export interface CheckInAttendanceInput {
  readonly eventInstanceId: string;
  readonly token?: string;
  readonly enrollmentId?: string;
  readonly kind?: AttendanceEventKind;
  readonly occurredAt?: string;
  readonly clientReference?: string;
  readonly notes?: string;
  readonly source?: 'manual_entry' | 'manual_token';
  readonly metadata?: Readonly<Record<string, unknown>>;
}

export interface ListAttendanceEventsFilter {
  readonly eventInstanceId?: string;
  readonly enrollmentId?: string;
  readonly userId?: string;
  readonly status?: ReadonlyArray<'captured' | 'superseded' | 'voided'>;
  readonly kind?: ReadonlyArray<AttendanceEventKind>;
}

export interface CorrectAttendanceInput {
  readonly eventInstanceId: string;
  readonly enrollmentId: string;
  readonly kind: AttendanceEventKind;
  readonly occurredAt: string;
  readonly reason: string;
  readonly notes?: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly field?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function createApiClient(
  baseUrlOverride?: string,
  fetchImpl: typeof fetch = fetch,
): ApiClient {
  const baseUrl = trimTrailingSlash(
    baseUrlOverride ?? readEnvBaseUrl() ?? '/api',
  );

  const request = async <T>(
    session: ApiSession,
    method: string,
    path: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> => {
    const headers: Record<string, string> = {
      accept: 'application/json',
      'x-tenant-id': session.tenantCode,
    };
    if (session.devUser) {
      headers['x-dev-user'] = session.devUser;
    }
    const bearerToken = session.getBearerToken ? await session.getBearerToken() : session.bearerToken;
    if (bearerToken) {
      headers.authorization = `Bearer ${bearerToken}`;
    }
    if (session.locale) {
      headers['x-locale'] = session.locale;
    }
    if (body !== undefined) {
      headers['content-type'] = 'application/json';
    }
    const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
    const response = await fetchImpl(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal,
    });
    if (response.status === 204) {
      return undefined as T;
    }
    const text = await response.text();
    const parsed = text ? safeJsonParse(text) : undefined;
    if (!response.ok) {
      const problem = (parsed ?? {}) as {
        code?: string;
        message?: string;
        field?: string;
        detail?: string;
      };
      throw new ApiError(
        response.status,
        problem.code ?? `http_${response.status}`,
        problem.message ?? problem.detail ?? response.statusText,
        problem.field,
      );
    }
    return (parsed ?? {}) as T;
  };

  const requestBinary = async (
    session: ApiSession,
    method: string,
    path: string,
    options?: { readonly signal?: AbortSignal },
  ): Promise<DownloadedFile> => {
    const headers: Record<string, string> = {
      accept: '*/*',
      'x-tenant-id': session.tenantCode,
    };
    if (session.devUser) {
      headers['x-dev-user'] = session.devUser;
    }
    const bearerToken = session.getBearerToken ? await session.getBearerToken() : session.bearerToken;
    if (bearerToken) {
      headers.authorization = `Bearer ${bearerToken}`;
    }
    if (session.locale) {
      headers['x-locale'] = session.locale;
    }
    const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
    const response = await fetchImpl(url, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options?.signal ? { signal: options.signal } : {}),
    });
    if (!response.ok) {
      const text = await response.text();
      const parsed = text ? safeJsonParse(text) : undefined;
      const problem = (parsed ?? {}) as {
        code?: string;
        message?: string;
        field?: string;
        detail?: string;
      };
      throw new ApiError(
        response.status,
        problem.code ?? `http_${response.status}`,
        problem.message ?? problem.detail ?? response.statusText,
        problem.field,
      );
    }
    return {
      filename:
        parseFilename(
          response.headers.get('content-disposition'),
        ) ?? 'report.bin',
      contentType:
        response.headers.get('content-type') ??
        'application/octet-stream',
      blob: await response.blob(),
    };
  };

  return {
    async getAuthContext(tenantCode) {
      const session: ApiSession = { tenantCode: tenantCode ?? 'eDO' };
      return request<AuthContextResponse>(session, 'GET', '/auth/context');
    },
    getMe(session) {
      return request<MeResponse>(session, 'GET', '/me');
    },
    getAccount(session) { return request(session, 'GET', '/account'); },
    getOrganizationBranding(session) { return request(session, 'GET', '/organization/branding'); },
    getAdminOrganizationBranding(session) { return request(session, 'GET', '/admin/organization/branding'); },
    saveOrganizationBranding(session, input) { return request(session, 'PUT', '/admin/organization/branding', input); },
    applyOrganizationBranding(session, input) { return request(session, 'POST', '/admin/organization/branding/apply', input); },
    uploadBrandingAsset(session, input, options) { return request(session, 'POST', '/admin/organization/branding/assets', input, options?.signal); },
    async getBrandingAsset(session, id, options) { return (await requestBinary(session, 'GET', `/organization/branding/assets/${encodeURIComponent(id)}`, options)).blob; },
    createOrganization(session, input) { return request(session, 'POST', '/account/organizations', input); },
    listOrganizationMembers(session) { return request(session, 'GET', '/admin/organization/members'); },
    changeOrganizationMember(session, input) { return request(session, 'POST', '/admin/organization/members', input); },
    listOrganizationRoles(session) { return request(session, 'GET', '/admin/organization/members/roles'); },
    listPrivacyRoles(session) { return request(session, 'GET', '/admin/organization/privacy/roles'); },
    changePrivacyOfficer(session, input) { return request(session, 'POST', '/admin/organization/members/privacy-officer', input); },
    listPrivacyPolicies(session) { return request(session, 'GET', '/admin/organization/privacy'); },
    getPrivacyPolicy(session, kind) { return request(session, 'GET', `/admin/organization/privacy/policies/${encodeURIComponent(kind)}`); },
    savePrivacyRevision(session, kind, input) { return request(session, 'POST', `/admin/organization/privacy/policies/${encodeURIComponent(kind)}/revisions`, input); },
    decidePrivacyPolicy(session, kind, input) { return request(session, 'POST', `/admin/organization/privacy/policies/${encodeURIComponent(kind)}/decisions`, input); },
    getMeAccessDiagnostics(session) {
      return request<MeAccessDiagnosticsResponse>(
        session,
        'GET',
        '/me/access-diagnostics',
      );
    },

    async listPlatformTenants(session) {
      const envelope = await request<{ items: PlatformTenantRecord[] }>(
        session,
        'GET',
        '/platform/tenants',
      );
      return envelope.items;
    },
    getPlatformTenant(session, id) {
      return request<PlatformTenantRecord>(
        session,
        'GET',
        `/platform/tenants/${encodeURIComponent(id)}`,
      );
    },
    createPlatformTenant(session, input) {
      return request<PlatformTenantRecord>(
        session,
        'POST',
        '/platform/tenants',
        input,
      );
    },
    updatePlatformTenantStatus(session, id, status) {
      return request<PlatformTenantRecord>(
        session,
        'PATCH',
        `/platform/tenants/${encodeURIComponent(id)}/status`,
        { status },
      );
    },

    async listEventTypes(session) {
      const envelope = await request<ListEnvelope<EventTypeRecord>>(
        session,
        'GET',
        '/events/types',
      );
      return envelope.items;
    },
    saveEventType(session, code, input) {
      return request<EventTypeRecord>(session, 'PUT', `/events/types/${encodeURIComponent(code)}`, input);
    },
    async listEvents(session, filter) {
      const params = new URLSearchParams();
      if (filter?.state?.length) {
        params.set('state', filter.state.join(','));
      }
      const qs = params.toString();
      const envelope = await request<ListEnvelope<EventInstanceRecord>>(
        session,
        'GET',
        `/events/instances${qs ? `?${qs}` : ''}`,
      );
      return envelope.items;
    },
    getEvent(session, id) {
      return request(session, 'GET', `/events/instances/${encodeURIComponent(id)}`);
    },
    saveEventResponsible(session, eventId, input) {
      return request(session, 'PUT', `/events/instances/${encodeURIComponent(eventId)}/responsibles`, input);
    },
    saveScheduleItem(session, eventId, input, itemId) {
      return request(session, itemId ? 'PUT' : 'POST',
        `/events/instances/${encodeURIComponent(eventId)}/schedule${itemId ? `/${encodeURIComponent(itemId)}` : ''}`, input);
    },
    createEvent(session, input) {
      return request(session, 'POST', '/events/instances', input);
    },
    updateEvent(session, id, patch) {
      return request(
        session,
        'PUT',
        `/events/instances/${encodeURIComponent(id)}`,
        patch,
      );
    },
    transitionEvent(session, id, to) {
      return request(
        session,
        'POST',
        `/events/instances/${encodeURIComponent(id)}/transitions`,
        { to },
      );
    },

    async listEnrollments(session, filter) {
      const params = new URLSearchParams();
      if (filter.eventInstanceId) {
        params.set('eventInstanceId', filter.eventInstanceId);
      }
      if (filter.userId) {
        params.set('userId', filter.userId);
      }
      if (filter.status?.length) {
        params.set('status', filter.status.join(','));
      }
      const envelope = await request<ListEnvelope<EnrollmentRecord>>(
        session,
        'GET',
        `/enrollments?${params.toString()}`,
      );
      return envelope.items;
    },
    async listMyEnrollments(session) {
      const envelope = await request<ListEnvelope<EnrollmentRecord>>(
        session,
        'GET',
        '/enrollments/mine',
      );
      return envelope.items;
    },
    enrollSelf(session, eventInstanceId) {
      return request(session, 'POST', '/enrollments', { eventInstanceId });
    },
    enrollAdmin(session, input) {
      return request(session, 'POST', '/enrollments/admin-assist', input);
    },
    cancelEnrollment(session, id, reason) {
      return request(
        session,
        'POST',
        `/enrollments/${encodeURIComponent(id)}/cancel`,
        reason ? { reason } : {},
      );
    },
    issueToken(session, enrollmentId) {
      return request(
        session,
        'POST',
        `/enrollments/${encodeURIComponent(enrollmentId)}/tokens`,
      );
    },

    listAttendanceSessions(session, eventId) { return request(session, 'GET', `/attendance/runs/${encodeURIComponent(eventId)}/sessions`); },
    createAttendanceSession(session, eventId, scheduleItemId) { return request(session, 'POST', `/attendance/runs/${encodeURIComponent(eventId)}/sessions`, { scheduleItemId }); },
    openAttendanceSession(session, id, expectedRevision) { return request(session, 'POST', `/attendance/sessions/${encodeURIComponent(id)}/open`, { expectedRevision }); },
    closeAttendanceSession(session, id, input) { return request(session, 'POST', `/attendance/sessions/${encodeURIComponent(id)}/close`, input); },
    reopenAttendanceSession(session, id, input) { return request(session, 'POST', `/attendance/sessions/${encodeURIComponent(id)}/reopen`, input); },
    cancelAttendanceSession(session, id, input) { return request(session, 'POST', `/attendance/sessions/${encodeURIComponent(id)}/cancel`, input); },
    getAttendanceSessionRoster(session, id) { return request(session, 'GET', `/attendance/sessions/${encodeURIComponent(id)}/roster`); },
    addAttendanceSessionParticipant(session, id, input) { return request(session, 'POST', `/attendance/sessions/${encodeURIComponent(id)}/roster`, input); },
    correctAttendanceSessionRecord(session, id, enrollmentId, input) { return request(session, 'POST', `/attendance/sessions/${encodeURIComponent(id)}/records/${encodeURIComponent(enrollmentId)}`, input); },
    checkInAttendanceSession(session, id, input) { return request(session, 'POST', `/attendance/sessions/${encodeURIComponent(id)}/check-ins`, input, AbortSignal.timeout(15000)); },
    getAttendanceSessionCheckIn(session, id, operationId) { return request(session, 'GET', `/attendance/sessions/${encodeURIComponent(id)}/check-ins/${encodeURIComponent(operationId)}`, undefined, AbortSignal.timeout(15000)); },
    getAttendanceRunMatrix(session, eventId) { return request(session, 'GET', `/reporting/events/${encodeURIComponent(eventId)}/attendance-sessions`); },
    downloadAttendanceRunCsv(session, eventId) { return requestBinary(session, 'GET', `/reporting/events/${encodeURIComponent(eventId)}/attendance-sessions/exports/csv`); },
    getAttendanceRunPrintSnapshot(session, eventId) { return request(session, 'GET', `/reporting/events/${encodeURIComponent(eventId)}/attendance-sessions/exports/print`); },
    emailEnrollmentCredential(session, enrollmentId, requestId) { return request(session, 'POST', `/enrollments/${encodeURIComponent(enrollmentId)}/tokens/email`, { requestId }); },
    emailEnrollmentCredentialsBatch(session, enrollmentIds, requestId) { return request(session, 'POST', '/enrollments/tokens/email-batch', { enrollmentIds, requestId }); },
    rotateEnrollmentCredential(session, enrollmentId, reason) { return request(session, 'POST', `/enrollments/${encodeURIComponent(enrollmentId)}/tokens/rotate`, { reason }); },
    revokeEnrollmentCredential(session, enrollmentId, reason) { return request(session, 'POST', `/enrollments/${encodeURIComponent(enrollmentId)}/tokens/revoke`, { reason }); },
    checkInAttendance(session, input) {
      return request(session, 'POST', '/attendance/check-ins', input);
    },
    async listAttendanceEvents(session, filter) {
      const params = new URLSearchParams();
      if (filter.eventInstanceId) params.set('eventInstanceId', filter.eventInstanceId);
      if (filter.enrollmentId) params.set('enrollmentId', filter.enrollmentId);
      if (filter.userId) params.set('userId', filter.userId);
      if (filter.status?.length) params.set('status', filter.status.join(','));
      if (filter.kind?.length) params.set('kind', filter.kind.join(','));
      const envelope = await request<ListEnvelope<AttendanceEventRecord>>(
        session,
        'GET',
        `/attendance/events?${params.toString()}`,
      );
      return envelope.items;
    },
    getAttendanceSummary(session, eventInstanceId) {
      return request(
        session,
        'GET',
        `/attendance/summaries/${encodeURIComponent(eventInstanceId)}`,
      );
    },
    getAttendancePolicy(session, eventInstanceId) {
      return request(
        session,
        'GET',
        `/attendance/events/${encodeURIComponent(eventInstanceId)}/policy`,
      );
    },
    upsertAttendancePolicy(session, eventInstanceId, input) {
      return request(
        session,
        'PUT',
        `/attendance/events/${encodeURIComponent(eventInstanceId)}/policy`,
        input,
      );
    },
    removeAttendancePolicy(session, eventInstanceId) {
      return request(
        session,
        'DELETE',
        `/attendance/events/${encodeURIComponent(eventInstanceId)}/policy`,
      );
    },
    correctAttendance(session, input) {
      return request(session, 'POST', '/attendance/corrections', input);
    },
    voidAttendance(session, attendanceEventId, reason) {
      return request(
        session,
        'POST',
        `/attendance/events/${encodeURIComponent(attendanceEventId)}/void`,
        { reason },
      );
    },
    async listAttendanceCorrections(session, filter) {
      const params = new URLSearchParams();
      if (filter.eventInstanceId) params.set('eventInstanceId', filter.eventInstanceId);
      if (filter.enrollmentId) params.set('enrollmentId', filter.enrollmentId);
      const envelope = await request<ListEnvelope<AttendanceCorrectionRecord>>(
        session,
        'GET',
        `/attendance/corrections?${params.toString()}`,
      );
      return envelope.items;
    },

    async listMyTasks(session, enrollmentId) {
      const envelope = await request<ListEnvelope<ParticipantTaskView>>(
        session,
        'GET',
        `/tasks/enrollments/${encodeURIComponent(enrollmentId)}/tasks`,
      );
      return envelope.items;
    },
    getMyTask(session, enrollmentId, assignmentId) {
      return request(
        session,
        'GET',
        `/tasks/enrollments/${encodeURIComponent(enrollmentId)}/tasks/${encodeURIComponent(assignmentId)}`,
      );
    },
    saveMyTask(session, enrollmentId, assignmentId, input) {
      return request(
        session,
        'POST',
        `/tasks/enrollments/${encodeURIComponent(enrollmentId)}/tasks/${encodeURIComponent(assignmentId)}/save`,
        input,
      );
    },
    submitTest(session, enrollmentId, assignmentId, answers) {
      return request(
        session,
        'POST',
        `/tasks/enrollments/${encodeURIComponent(enrollmentId)}/tasks/${encodeURIComponent(assignmentId)}/submit-test`,
        { answers },
      );
    },
    submitDocument(session, enrollmentId, assignmentId, note) {
      return request(
        session,
        'POST',
        `/tasks/enrollments/${encodeURIComponent(enrollmentId)}/tasks/${encodeURIComponent(assignmentId)}/submit-document`,
        note ? { note } : {},
      );
    },
    attachUpload(session, enrollmentId, assignmentId, input) {
      return request(
        session,
        'POST',
        `/tasks/enrollments/${encodeURIComponent(enrollmentId)}/tasks/${encodeURIComponent(assignmentId)}/uploads`,
        input,
      );
    },
    async removeUpload(session, enrollmentId, uploadId) {
      await request(
        session,
        'DELETE',
        `/tasks/enrollments/${encodeURIComponent(enrollmentId)}/uploads/${encodeURIComponent(uploadId)}`,
      );
    },

    async listReviewQueue(session, filter) {
      const params = new URLSearchParams();
      if (filter?.eventInstanceId)
        params.set('eventInstanceId', filter.eventInstanceId);
      const qs = params.toString();
      const envelope = await request<ListEnvelope<ReviewerTaskView>>(
        session,
        'GET',
        `/tasks/reviews/queue${qs ? `?${qs}` : ''}`,
      );
      return envelope.items;
    },
    getTaskRuntimeDiagnostics(session) {
      return request(session, 'GET', '/tasks/readiness');
    },
    getReviewSubmission(session, submissionId) {
      return request(
        session,
        'GET',
        `/tasks/submissions/${encodeURIComponent(submissionId)}`,
      );
    },
    recordReviewDecision(session, submissionId, input) {
      return request(
        session,
        'POST',
        `/tasks/submissions/${encodeURIComponent(submissionId)}/decision`,
        input,
      );
    },

    async listNotificationTemplates(session, filter) {
      const params = new URLSearchParams();
      if (filter?.channel) params.set('channel', filter.channel);
      if (filter?.enabled !== undefined)
        params.set('enabled', String(filter.enabled));
      const qs = params.toString();
      const envelope = await request<ListEnvelope<NotificationTemplateRecord>>(
        session,
        'GET',
        `/notifications/templates${qs ? `?${qs}` : ''}`,
      );
      return envelope.items;
    },
    async listNotificationJobs(session, filter) {
      const params = new URLSearchParams();
      if (filter?.status?.length)
        params.set('status', filter.status.join(','));
      if (filter?.triggerType)
        params.set('triggerType', filter.triggerType);
      if (filter?.triggerResourceId)
        params.set('triggerResourceId', filter.triggerResourceId);
      if (filter?.recipientUserId)
        params.set('recipientUserId', filter.recipientUserId);
      if (filter?.limit !== undefined)
        params.set('limit', String(filter.limit));
      const qs = params.toString();
      const envelope = await request<ListEnvelope<NotificationJobRecord>>(
        session,
        'GET',
        `/notifications/jobs${qs ? `?${qs}` : ''}`,
      );
      return envelope.items;
    },
    async listNotificationJobDeliveries(session, jobId) {
      const envelope = await request<ListEnvelope<NotificationDeliveryRecord>>(
        session,
        'GET',
        `/notifications/jobs/${encodeURIComponent(jobId)}/deliveries`,
      );
      return envelope.items;
    },
    async listNotificationDeliveries(session, filter) {
      const params = new URLSearchParams();
      if (filter?.status?.length)
        params.set('status', filter.status.join(','));
      if (filter?.limit !== undefined)
        params.set('limit', String(filter.limit));
      const qs = params.toString();
      const envelope = await request<ListEnvelope<NotificationDeliveryRecord>>(
        session,
        'GET',
        `/notifications/deliveries${qs ? `?${qs}` : ''}`,
      );
      return envelope.items;
    },
    cancelNotificationJob(session, id, reason) {
      return request(
        session,
        'POST',
        `/notifications/jobs/${encodeURIComponent(id)}/cancel`,
        { reason },
      );
    },

    listIdentityGroupMappings(session) {
      return request(session, 'GET', '/admin/identity/group-mappings');
    },
    createIdentityGroupMapping(session, input) {
      return request(session, 'POST', '/admin/identity/group-mappings', input);
    },
    updateIdentityGroupMapping(session, id, input) {
      return request(
        session,
        'PUT',
        `/admin/identity/group-mappings/${encodeURIComponent(id)}`,
        input,
      );
    },
    async listIdentityEffectiveAccess(session) {
      const envelope = await request<ListEnvelope<IdentityEffectiveAccessRecord>>(
        session,
        'GET',
        '/admin/identity/effective-access',
      );
      return envelope.items;
    },

    getEventReport(session, eventInstanceId) {
      return request(
        session,
        'GET',
        `/reporting/events/${encodeURIComponent(eventInstanceId)}`,
      );
    },
    downloadEventReportCsv(session, eventInstanceId) {
      return requestBinary(
        session,
        'GET',
        `/reporting/events/${encodeURIComponent(eventInstanceId)}/exports/csv`,
      );
    },
    downloadEventReportPdf(session, eventInstanceId) {
      return requestBinary(
        session,
        'GET',
        `/reporting/events/${encodeURIComponent(eventInstanceId)}/exports/pdf`,
      );
    },
    async listEdocationHandoffView(session, eventInstanceId) {
      const view = await request<EdocationHandoffListView>(
        session,
        'GET',
        `/integrations/edocation/events/${encodeURIComponent(eventInstanceId)}/handoffs`,
      );
      if (!view?.diagnostics || !Array.isArray(view.items) || !Array.isArray(view.diagnostics.caveatKeys)
        || typeof view.diagnostics.posture !== 'string') {
        throw new Error(session.locale === 'cs' ? 'Odpověď integrace není kompatibilní. Report zůstává dostupný; kontaktujte správce.' : 'The integration response is incompatible. Reporting remains available; contact an administrator.');
      }
      return view;
    },
    triggerEdocationHandoff(session, eventInstanceId) {
      return request(
        session,
        'POST',
        `/integrations/edocation/events/${encodeURIComponent(eventInstanceId)}/handoffs`,
      );
    },
  };
}

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function readEnvBaseUrl(): string | undefined {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_API_BASE_URL;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function parseFilename(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) return undefined;
  const match = /filename="([^"]+)"/i.exec(contentDisposition);
  return match?.[1];
}
